import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Brain, Heart, Dumbbell, Wind, BookOpen, Moon, Sparkles,
  Plus, Check, X, Share2, Users, Clock, Leaf, Pill,
  Wine, Cigarette, Coffee, Star, Timer, Send, UserPlus
} from 'lucide-react';
import { TypewriterMessage } from '@/components/onboarding/TypewriterMessage';
import GratitudeExercises from '@/components/gratitude/GratitudeExercises';

// Default self-care tools with icons
const DEFAULT_TOOLS = [
  { id: 'meditation', name: 'Meditation', icon: Brain, category: 'mindfulness', defaultDuration: 15 },
  { id: 'yoga', name: 'Yoga', icon: Sparkles, category: 'movement', defaultDuration: 30 },
  { id: 'journaling', name: 'Journaling', icon: BookOpen, category: 'reflection', defaultDuration: 10 },
  { id: 'breathwork', name: 'Breathwork', icon: Wind, category: 'mindfulness', defaultDuration: 10 },
  { id: 'walking', name: 'Walking', icon: Dumbbell, category: 'movement', defaultDuration: 30 },
  { id: 'cardio', name: 'Cardio', icon: Heart, category: 'movement', defaultDuration: 30 },
  { id: 'strength_training', name: 'Strength Training', icon: Dumbbell, category: 'movement', defaultDuration: 45 },
  { id: 'gratitude', name: 'Gratitude Practice', icon: Star, category: 'reflection', defaultDuration: 5 },
  { id: 'nature_time', name: 'Time in Nature', icon: Leaf, category: 'grounding', defaultDuration: 20 },
  { id: 'eft_tapping', name: 'EFT/Tapping', icon: Heart, category: 'therapeutic', defaultDuration: 15 },
  { id: 'sleep_hygiene', name: 'Sleep Hygiene Routine', icon: Moon, category: 'rest', defaultDuration: 20 },
];

// Positive behaviours to track
const POSITIVE_BEHAVIORS = [
  { id: 'supplements', name: 'Took Supplements', icon: Pill },
  { id: 'hrt', name: 'Took HRT', icon: Pill },
  { id: 'medication', name: 'Took Medication', icon: Pill },
  { id: 'hydration', name: 'Drank Enough Water', icon: Leaf },
  { id: 'healthy_meal', name: 'Ate a Healthy Meal', icon: Leaf },
  { id: 'social_connection', name: 'Connected with Someone', icon: Users },
  { id: 'therapy_homework', name: 'Did Therapy Homework', icon: BookOpen },
];

// Negative behaviours avoided
const NEGATIVE_BEHAVIORS = [
  { id: 'no_alcohol', name: 'No Alcohol', icon: Wine },
  { id: 'no_cigarette', name: 'No Cigarette', icon: Cigarette },
  { id: 'no_excess_caffeine', name: 'Limited Caffeine', icon: Coffee },
  { id: 'no_doomscrolling', name: 'Avoided Doomscrolling', icon: Brain },
  { id: 'no_emotional_eating', name: 'No Emotional Eating', icon: Heart },
];

interface ToolUsage {
  toolId: string;
  duration: number;
  completed: boolean;
}

interface BehaviorCheck {
  behaviorId: string;
  checked: boolean;
}

interface CustomTool {
  id: string;
  name: string;
  category: string;
  defaultDuration: number;
}

export default function EveningSelfCareCheckIn() {
  const queryClient = useQueryClient();
  const [toolUsages, setToolUsages] = useState<Record<string, ToolUsage>>({});
  const [positiveBehaviors, setPositiveBehaviors] = useState<Record<string, boolean>>({});
  const [negativeBehaviors, setNegativeBehaviors] = useState<Record<string, boolean>>({});
  const [customTools, setCustomTools] = useState<CustomTool[]>([]);
  const [newToolName, setNewToolName] = useState('');
  const [newToolDuration, setNewToolDuration] = useState(15);
  const [isAddingTool, setIsAddingTool] = useState(false);
  const [shareWithTherapist, setShareWithTherapist] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  // Fetch user
  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    }
  });

  // Load custom tools from localStorage
  useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem(`therappi_custom_tools_${user.id}`);
    if (stored) {
      try {
        setCustomTools(JSON.parse(stored) as CustomTool[]);
      } catch (e) {
        console.error('Error parsing custom tools:', e);
      }
    }
  }, [user]);

  const allTools = [...DEFAULT_TOOLS, ...customTools.map(t => ({ ...t, icon: Sparkles }))];

  const handleToolToggle = (toolId: string) => {
    setToolUsages(prev => {
      if (prev[toolId]) {
        const { [toolId]: _, ...rest } = prev;
        return rest;
      }
      const tool = allTools.find(t => t.id === toolId);
      return {
        ...prev,
        [toolId]: {
          toolId,
          duration: tool?.defaultDuration || 15,
          completed: true
        }
      };
    });
  };

  const handleDurationChange = (toolId: string, duration: number) => {
    setToolUsages(prev => ({
      ...prev,
      [toolId]: { ...prev[toolId], duration }
    }));
  };

  const addCustomTool = async () => {
    if (!newToolName.trim() || !user) return;
    
    const newTool: CustomTool = {
      id: `custom_${Date.now()}`,
      name: newToolName.trim(),
      category: 'custom',
      defaultDuration: newToolDuration
    };
    
    const updatedTools = [...customTools, newTool];
    setCustomTools(updatedTools);
    
    // Save to localStorage
    localStorage.setItem(`therappi_custom_tools_${user.id}`, JSON.stringify(updatedTools));
    
    setNewToolName('');
    setNewToolDuration(15);
    setIsAddingTool(false);
    toast.success(`Added "${newTool.name}" to your tools`);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      
      // Log each tool usage as wellness activity
      const toolEntries = Object.values(toolUsages).filter(t => t.completed);
      
      for (const entry of toolEntries) {
        await supabase.from('wellness_activities').insert({
          user_id: user.id,
          activity_type: entry.toolId,
          duration_minutes: entry.duration,
          started_at: new Date().toISOString(),
          notes: 'Logged from evening self-care check-in',
          external_activity: true
        });
      }
      
      // Log behaviours to daily check-in or a behaviours table
      const behaviorData = {
        user_id: user.id,
        date: new Date().toISOString().split('T')[0],
        positive_behaviors: Object.entries(positiveBehaviors)
          .filter(([_, v]) => v)
          .map(([k]) => k),
        negative_behaviors_avoided: Object.entries(negativeBehaviors)
          .filter(([_, v]) => v)
          .map(([k]) => k),
        tools_used: toolEntries.map(t => ({ id: t.toolId, duration: t.duration })),
        shared_with_therapist: shareWithTherapist
      };
      
      // Store in therapeutic exercise logs for clinician visibility
      if (shareWithTherapist) {
        await supabase.from('therapeutic_exercise_logs').insert({
          user_id: user.id,
          exercise_type: 'evening_self_care',
          exercise_name: 'Evening Self-Care Check-In',
          category: 'self_care',
          duration_minutes: toolEntries.reduce((sum, t) => sum + t.duration, 0),
          completion_data: behaviorData,
          xp_earned: Math.min(toolEntries.length * 5 + 
            Object.values(positiveBehaviors).filter(Boolean).length * 3 +
            Object.values(negativeBehaviors).filter(Boolean).length * 5, 50)
        });
      }
      
      return behaviorData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['wellness-activities'] });
      queryClient.invalidateQueries({ queryKey: ['exercise-stats'] });
      
      const toolCount = Object.keys(toolUsages).length;
      const positiveCount = Object.values(positiveBehaviors).filter(Boolean).length;
      const negativeCount = Object.values(negativeBehaviors).filter(Boolean).length;
      
      toast.success(
        `Evening check-in saved! ${toolCount} tools used, ${positiveCount + negativeCount} healthy choices tracked`,
        { description: shareWithTherapist ? 'Shared with your therapist' : undefined }
      );
      
      // Reset form
      setToolUsages({});
      setPositiveBehaviors({});
      setNegativeBehaviors({});
    },
    onError: (error) => {
      console.error('Error saving check-in:', error);
      toast.error('Failed to save check-in');
    }
  });

  const sendAccountabilityInvite = async () => {
    if (!inviteEmail || !user) return;
    
    try {
      await supabase.functions.invoke('send-accountability-invite', {
        body: {
          recipient_email: inviteEmail,
          sender_name: user.user_metadata?.display_name || 'A Therappi user',
          message: `I'd love you to be my accountability buddy on Therappi! Join me to help track our wellness goals together.`
        }
      });
      
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteDialogOpen(false);
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error('Failed to send invitation');
    }
  };

  const totalDuration = Object.values(toolUsages).reduce((sum, t) => sum + t.duration, 0);
  const toolCount = Object.keys(toolUsages).length;
  const positiveCount = Object.values(positiveBehaviors).filter(Boolean).length;
  const negativeCount = Object.values(negativeBehaviors).filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Header with Typewriter */}
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Moon className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Evening Self-Care Check-In</h2>
        <div className="text-muted-foreground min-h-[3rem]">
          <TypewriterMessage 
            text="Taking a moment to reflect on your day helps your brain recognise the positive steps you're taking. Research shows that tracking self-care builds lasting habits and improves outcomes."
            speed={20}
            showCursor={false}
          />
        </div>
      </div>

      <Tabs defaultValue="tools" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="tools" className="gap-2">
            <Brain className="w-4 h-4" />
            Tools
          </TabsTrigger>
          <TabsTrigger value="positive" className="gap-2">
            <Check className="w-4 h-4" />
            Positives
          </TabsTrigger>
          <TabsTrigger value="negative" className="gap-2">
            <X className="w-4 h-4" />
            Avoided
          </TabsTrigger>
          <TabsTrigger value="gratitude" className="gap-2">
            <Star className="w-4 h-4" />
            Gratitude
          </TabsTrigger>
        </TabsList>

        {/* Tools Tab */}
        <TabsContent value="tools" className="space-y-4 mt-4">
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 mb-4">
            <p className="text-sm text-primary">
              <TypewriterMessage 
                text="Each tool you log creates a pattern your AI therapist can use to understand what helps you most. The more you track, the more personalised your insights become."
                speed={15}
                delay={500}
                showCursor={false}
              />
            </p>
          </div>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Self-Care Tools</h3>
              <Dialog open={isAddingTool} onOpenChange={setIsAddingTool}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Plus className="w-4 h-4" />
                    Add Custom Tool
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Custom Tool</DialogTitle>
                    <DialogDescription>
                      Add a self-care practice you'd like to track
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Tool Name</Label>
                      <Input
                        value={newToolName}
                        onChange={(e) => setNewToolName(e.target.value)}
                        placeholder="e.g., Cold Shower, Reading, Prayer"
                      />
                    </div>
                    <div>
                      <Label>Default Duration (minutes): {newToolDuration}</Label>
                      <Slider
                        value={[newToolDuration]}
                        onValueChange={([v]) => setNewToolDuration(v)}
                        min={5}
                        max={120}
                        step={5}
                        className="mt-2"
                      />
                    </div>
                    <Button onClick={addCustomTool} className="w-full">
                      Add Tool
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-3">
                {allTools.map((tool) => {
                  const isActive = !!toolUsages[tool.id];
                  const IconComponent = tool.icon;
                  
                  return (
                    <div
                      key={tool.id}
                      className={`p-4 rounded-lg border transition-all ${
                        isActive 
                          ? 'bg-primary/10 border-primary' 
                          : 'bg-muted/30 border-border hover:border-primary/50'
                      }`}
                    >
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => handleToolToggle(tool.id)}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{tool.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{tool.category}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isActive ? 'bg-primary border-primary' : 'border-muted-foreground'
                        }`}>
                          {isActive && <Check className="w-4 h-4 text-primary-foreground" />}
                        </div>
                      </div>
                      
                      {isActive && (
                        <div className="mt-4 pl-13">
                          <Label className="text-sm flex items-center gap-2">
                            <Timer className="w-4 h-4" />
                            Duration: {toolUsages[tool.id]?.duration} minutes
                          </Label>
                          <Slider
                            value={[toolUsages[tool.id]?.duration || 15]}
                            onValueChange={([v]) => handleDurationChange(tool.id, v)}
                            min={5}
                            max={120}
                            step={5}
                            className="mt-2"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </Card>
        </TabsContent>

        {/* Positive Behaviours Tab */}
        <TabsContent value="positive" className="space-y-4 mt-4">
          <div className="p-3 bg-accent/10 rounded-lg border border-accent/20 mb-4">
            <p className="text-sm text-accent-foreground">
              <TypewriterMessage 
                text="Every positive action, no matter how small, strengthens neural pathways for wellbeing. Checking these off isn't just tracking - it's training your brain to notice what you're doing right."
                speed={15}
                delay={300}
                showCursor={false}
              />
            </p>
          </div>
          <Card className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Check className="w-5 h-5 text-accent" />
              Positive Actions Today
            </h3>
            <div className="grid grid-cols-1 gap-3">
              {POSITIVE_BEHAVIORS.map((behavior) => {
                const isChecked = positiveBehaviors[behavior.id];
                const IconComponent = behavior.icon;
                
                return (
                  <div
                    key={behavior.id}
                    onClick={() => setPositiveBehaviors(prev => ({
                      ...prev,
                      [behavior.id]: !prev[behavior.id]
                    }))}
                    className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${
                      isChecked 
                        ? 'bg-accent/20 border-accent' 
                        : 'bg-muted/30 border-border hover:border-accent/50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isChecked ? 'bg-accent text-accent-foreground' : 'bg-muted'
                    }`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="font-medium flex-1">{behavior.name}</span>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isChecked ? 'bg-accent border-accent' : 'border-muted-foreground'
                    }`}>
                      {isChecked && <Check className="w-4 h-4 text-accent-foreground" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* Negative Behaviours Avoided Tab */}
        <TabsContent value="negative" className="space-y-4 mt-4">
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 mb-4">
            <p className="text-sm text-primary">
              <TypewriterMessage 
                text="What we choose NOT to do can be just as important as what we do. Each time you avoid a harmful behavior, you're proving to yourself that you have control. This data helps your therapist understand your progress."
                speed={15}
                delay={300}
                showCursor={false}
              />
            </p>
          </div>
          <Card className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-primary" />
              Healthy Choices (What You Avoided)
            </h3>
            <div className="grid grid-cols-1 gap-3">
              {NEGATIVE_BEHAVIORS.map((behavior) => {
                const isChecked = negativeBehaviors[behavior.id];
                const IconComponent = behavior.icon;
                
                return (
                  <div
                    key={behavior.id}
                    onClick={() => setNegativeBehaviors(prev => ({
                      ...prev,
                      [behavior.id]: !prev[behavior.id]
                    }))}
                    className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${
                      isChecked 
                        ? 'bg-primary/20 border-primary' 
                        : 'bg-muted/30 border-border hover:border-primary/50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isChecked ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="font-medium flex-1">{behavior.name}</span>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isChecked ? 'bg-primary border-primary' : 'border-muted-foreground'
                    }`}>
                      {isChecked && <Check className="w-4 h-4 text-primary-foreground" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* Gratitude Tab */}
        <TabsContent value="gratitude" className="space-y-4 mt-4">
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 mb-4">
            <p className="text-sm text-primary">
              <TypewriterMessage 
                text="Gratitude practices rewire your brain for positivity. Research shows just 3 minutes of daily gratitude can improve wellbeing for months."
                speed={15}
                delay={300}
                showCursor={false}
              />
            </p>
          </div>
          <GratitudeExercises 
            exerciseType="rose-bud-thorn"
            onComplete={(data) => {
              toast.success('Gratitude exercise completed!');
              // Could save to database here
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Summary Card */}
      <Card className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Today's Summary</h3>
          <div className="flex gap-2">
            <Badge variant="secondary">{toolCount} tools</Badge>
            <Badge variant="secondary">{totalDuration} mins</Badge>
            <Badge className="bg-accent">{positiveCount + negativeCount} wins</Badge>
          </div>
        </div>
        
        {/* Share with Therapist Toggle */}
        <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg mb-4">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-primary" />
            <div>
              <p className="font-medium text-sm">Share with Therapist</p>
              <p className="text-xs text-muted-foreground">Your clinician will see this check-in</p>
            </div>
          </div>
          <Switch
            checked={shareWithTherapist}
            onCheckedChange={setShareWithTherapist}
          />
        </div>

        {/* Accountability Buddy Section */}
        <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg mb-4">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-secondary" />
            <div>
              <p className="font-medium text-sm">Accountability Buddy</p>
              <p className="text-xs text-muted-foreground">Invite someone to support you</p>
            </div>
          </div>
          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <UserPlus className="w-4 h-4" />
                Invite
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Accountability Buddy</DialogTitle>
                <DialogDescription>
                  Invite someone to join you on Therappi and be your accountability partner
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Their Email Address</Label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="friend@example.com"
                  />
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-sm">
                  <p className="font-medium mb-1">They'll receive:</p>
                  <p className="text-muted-foreground">
                    "{user?.user_metadata?.display_name || 'Your friend'} is sending you a request to join them on Therappi and to be their accountability buddy!"
                  </p>
                </div>
                <Button onClick={sendAccountabilityInvite} className="w-full gap-2">
                  <Send className="w-4 h-4" />
                  Send Invitation
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Button 
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || (toolCount === 0 && positiveCount === 0 && negativeCount === 0)}
          className="w-full gap-2"
          size="lg"
        >
          {saveMutation.isPending ? (
            'Saving...'
          ) : (
            <>
              <Check className="w-5 h-5" />
              Complete Evening Check-In
            </>
          )}
        </Button>
      </Card>
    </div>
  );
}
